(() => {
  'use strict';
  if (!/^da(?:-|$)/i.test(document.documentElement.lang)) return;
  const marker = document.getElementById('a08-calendar-da-loader');
  if (!marker || marker.dataset.started) return;
  marker.dataset.started = '1';
  const expectedHash = '8a75d7486eb8ab71db9df26acb8e6e7e4715d13cd2e49f4df46bdebd5120e71a';
  const targetId = 'streamthing_flatpickr_target';
  const processed = new WeakSet();
  const months = 'January February March April May June July August September October November December'.split(' ');
  let activeTarget = null;
  let activeInstance = null;
  let timer = null;
  let deadline = 0;
  let proof = null;
  const status = value => { marker.dataset.status = value; };
  function serialize(date) {
    const d = date.getDate();
    const suffix = d % 100 >= 11 && d % 100 <= 13 ? 'th' : ({1:'st',2:'nd',3:'rd'}[d % 10] || 'th');
    return `${months[date.getMonth()]} ${d}${suffix}, ${date.getFullYear()}`;
  }
  async function verifySource() {
    const scripts = Array.from(document.scripts).filter(s => {
      try { const u = new URL(s.src); return u.origin === 'https://app.delivery.handyjs.org' && u.pathname === '/js/deliveryapp_scripttag.min.js'; } catch (_) { return false; }
    });
    if (scripts.length !== 1 || !crypto.subtle) return false;
    const controller = new AbortController();
    const abortTimer = setTimeout(() => controller.abort(), 8000);
    try {
      const r = await fetch(scripts[0].src, {credentials:'omit', signal:controller.signal});
      if (!r.ok) return false;
      const digest = await crypto.subtle.digest('SHA-256', await r.arrayBuffer());
      return Array.from(new Uint8Array(digest), b => b.toString(16).padStart(2,'0')).join('') === expectedHash;
    } catch (_) { return false; } finally { clearTimeout(abortTimer); }
  }
  function supported(fp) {
    try {
    if (!fp || typeof fp.set !== 'function' || typeof fp.formatDate !== 'function') return false;
    const c = fp.config;
    const empty = ['onValueUpdate','onDayCreate','onOpen','onClose','onYearChange','onReady','onParseConfig','onDestroy','onKeyDown','onPreCalendarPosition'];
    return c && c.mode === 'single' && c.wrap === true && c.altInput === false && c.allowInput === false &&
      c.enableTime === false && c.noCalendar === false && c.inline === false && !fp.mobileInput &&
      Array.isArray(fp.selectedDates) && fp.selectedDates.length <= 1 && fp.selectedDates.every(d => d instanceof Date && !Number.isNaN(d.getTime())) &&
      c.dateFormat === 'F J, Y' && c.ariaDateFormat === 'F j, Y' && c.showMonths === 1 && c.monthSelectorType === 'static' &&
      Array.isArray(c.onChange) && c.onChange.length === 1 && typeof c.onChange[0] === 'function' &&
      Array.isArray(c.onMonthChange) && c.onMonthChange.length === 1 && typeof c.onMonthChange[0] === 'function' &&
      empty.every(k => Array.isArray(c[k]) && c[k].length === 0) &&
      fp.element === document.getElementById(targetId) && fp.input && fp.element.contains(fp.input) && fp.input.id === 'datepicker' && fp.input.readOnly && fp.calendarContainer && fp.calendarContainer.isConnected &&
      fp.l10n && fp.l10n.months && Array.isArray(fp.l10n.months.longhand) && fp.l10n.months.longhand.length === 12 &&
      fp.l10n.weekdays && Array.isArray(fp.l10n.weekdays.shorthand) && fp.l10n.weekdays.shorthand.length === 7 &&
      fp.l10n.months.longhand[9] === 'October' && fp.l10n.weekdays.shorthand[0] === 'Sun';
    } catch (_) { return false; }
  }
  function install(target, fp) {
    const c = fp.config;
    const original = c.onChange[0];
    const before = {locale:c.locale,dateFormat:c.dateFormat,ariaDateFormat:c.ariaDateFormat,time_24hr:c.time_24hr};
    const datesBefore = fp.selectedDates.map(d => d.getTime());
    const toggle = target.querySelector('[data-toggle]');
    const toggleTitle = toggle && toggle.getAttribute('title');
    const ariaNodes = [fp.currentYearElement,fp.prevMonthNav,fp.nextMonthNav,toggle].filter(Boolean);
    const ariaBefore = ariaNodes.map(n => [n,n.getAttribute('aria-label')]);
    let calls = 0;
    let rolledBack = false;
    const oldMarker = target.getAttribute('data-a08-locale');
    function dateInputEnter(event) {
      if (event.target !== fp.input || event.key !== 'Enter' || event.isComposing || !fp.input.readOnly || rolledBack) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      fp.open();
    }
    function rollback() {
      if (rolledBack) return;
      fp.input.removeEventListener('keydown',dateInputEnter,true);
      fp.set(before);
      const expectedInput = fp.selectedDates.length ? serialize(fp.selectedDates[0]) : '';
      if (c.dateFormat !== before.dateFormat || fp.input.value !== expectedInput || fp.l10n.months.longhand[9] !== 'October') throw new Error('rollback-postcondition');
      c.onChange[0] = original;
      rolledBack = true;
      if (oldMarker === null) target.removeAttribute('data-a08-locale'); else target.setAttribute('data-a08-locale',oldMarker);
      ariaBefore.forEach(([n,v]) => v === null ? n.removeAttribute('aria-label') : n.setAttribute('aria-label',v));
      if (toggle) toggleTitle === null ? toggle.removeAttribute('title') : toggle.setAttribute('title',toggleTitle);
      status('rolled-back');
    }
    function bridge(dates, dateStr, instance, ...extra) {
      if (dates.length > 1 || (dates.length && (!(dates[0] instanceof Date) || Number.isNaN(dates[0].getTime())))) {
        try { rollback(); } catch (_) { status('rollback-error'); }
        return;
      }
      calls += 1;
      marker.dataset.hookCalls = String(calls);
      return original.apply(this,[dates,dates.length ? serialize(dates[0]) : '',instance,...extra]);
    }
    const locale = {
      ...fp.l10n,
      weekdays:{shorthand:['søn','man','tir','ons','tor','fre','lør'],longhand:['søndag','mandag','tirsdag','onsdag','torsdag','fredag','lørdag']},
      months:{shorthand:['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'],longhand:['januar','februar','marts','april','maj','juni','juli','august','september','oktober','november','december']},
      ordinal:() => '.',
      firstDayOfWeek:1,
      time_24hr:c.time_24hr,
      yearAriaLabel:'År',monthAriaLabel:'Måned',scrollTitle:'Rul for at ændre',toggleTitle:'Klik for at skifte'
    };
    try {
      c.onChange[0] = bridge;
      fp.set({locale,dateFormat:'d/m/Y',ariaDateFormat:'j. F Y',time_24hr:before.time_24hr});
      if (toggle) { toggle.setAttribute('title','Åbn kalender'); toggle.setAttribute('aria-label','Åbn kalender'); }
      if (fp.currentYearElement) fp.currentYearElement.setAttribute('aria-label','År');
      if (fp.prevMonthNav) fp.prevMonthNav.setAttribute('aria-label','Forrige måned');
      if (fp.nextMonthNav) fp.nextMonthNav.setAttribute('aria-label','Næste måned');
      if (JSON.stringify(datesBefore) !== JSON.stringify(fp.selectedDates.map(d => d.getTime())) || calls !== 0 || c.onChange.length !== 1 || c.onChange[0] !== bridge) throw new Error('postcondition');
      target.dataset.a08Locale = 'da';
      marker.dataset.hookCalls = '0';
      marker.dataset.instances = String(Number(marker.dataset.instances || 0) + 1);
      fp.input.addEventListener('keydown',dateInputEnter,true);
      status('active');
    } catch (_) {
      try { rollback(); } catch (_) { status('rollback-error'); }
    }
  }
  async function attempt() {
    clearTimeout(timer);
    const target = document.getElementById(targetId);
    const fp = target && target._flatpickr;
    if (target !== activeTarget || fp !== activeInstance) { activeTarget = target; activeInstance = fp; deadline = Date.now() + 20000; }
    if (!target || (fp && processed.has(fp))) return;
    if (!fp || !fp.config || !fp.config.onChange || fp.config.onChange.length === 0) {
      if (Date.now() < deadline) timer = setTimeout(attempt,100);
      else { status('initialization-timeout'); }
      return;
    }
    if (!supported(fp)) { if (fp && (typeof fp === 'object' || typeof fp === 'function')) processed.add(fp); status('unsupported-instance'); return; }
    if (!proof) proof = verifySource().catch(() => false);
    const verified = await proof;
    if (!target.isConnected || document.getElementById(targetId) !== target || target._flatpickr !== fp || processed.has(fp)) return;
    processed.add(fp);
    if (!verified || !supported(fp)) { status(verified ? 'instance-changed' : 'unverified-app-version'); return; }
    install(target,fp);
  }
  new MutationObserver(() => {
    const target = document.getElementById(targetId);
    if (target !== activeTarget || (target && target._flatpickr) !== activeInstance) attempt();
  }).observe(document.body,{childList:true,subtree:true});
  status('waiting');
  attempt();
})();
